// Landing flair: the red-pen marks that ink themselves in as they scroll into
// view, feature cards that lean toward the pointer, resource studios whose
// tabs cycle by themselves, and the hero's pencil
// doodles – drawn with a hand-animated "boil" – with a paper plane that loops
// through now and then. All decorative; the CSS it drives lives under
// "Flair" in landing.css. Under reduced motion the marks are simply there,
// the doodles are drawn once, and nothing tilts or flies.
(function () {
    'use strict';

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const INK = '#1e1b2e';

    /* ── Red pen ────────────────────────────────────────── */
    // Inked once, when most of the mark is on screen, so it is drawn while
    // somebody is looking at it rather than while it is still at the fold.
    const pens = document.querySelectorAll('#landing-view .lp-pen');
    if (reduceMotion || !('IntersectionObserver' in window)) {
        pens.forEach(el => el.classList.add('is-inked'));
    } else {
        const penIO = new IntersectionObserver(entries => {
            entries.forEach(e => {
                if (!e.isIntersecting) return;
                e.target.classList.add('is-inked');
                penIO.unobserve(e.target);
            });
        }, { threshold: 0.9, rootMargin: '0px 0px -8% 0px' });
        pens.forEach(el => penIO.observe(el));
    }

    /* ── Card tilt ──────────────────────────────────────── */
    // Inline styles on purpose: they have to beat the reveal recipe's
    // transform, and its per-card transition delay, without a specificity
    // arms race in the stylesheet.
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (!reduceMotion && finePointer) {
        document.querySelectorAll('#landing-view .lp-card').forEach(card => {
            let leaveTimer = 0;
            card.addEventListener('pointermove', e => {
                const r = card.getBoundingClientRect();
                const px = (e.clientX - r.left) / r.width;
                const py = (e.clientY - r.top) / r.height;
                clearTimeout(leaveTimer);
                card.classList.add('is-tilting');
                card.style.transition = 'transform 0.12s ease-out, box-shadow 0.25s ease';
                card.style.transitionDelay = '0s';
                card.style.transform =
                    'perspective(900px) translateY(-6px) ' +
                    'rotateX(' + ((0.5 - py) * 9).toFixed(2) + 'deg) ' +
                    'rotateY(' + ((px - 0.5) * 11).toFixed(2) + 'deg)';
                card.style.boxShadow = (8 - (px - 0.5) * 8).toFixed(1) + 'px ' + (12 - (py - 0.5) * 6).toFixed(1) + 'px 0 rgba(30, 27, 46, 0.9)';
                card.style.setProperty('--gx', (px * 100).toFixed(1) + '%');
                card.style.setProperty('--gy', (py * 100).toFixed(1) + '%');
            });
            card.addEventListener('pointerleave', () => {
                card.classList.remove('is-tilting');
                // Settles back with a little overshoot, like it was let go of.
                card.style.transition = 'transform 0.6s cubic-bezier(0.34, 1.5, 0.64, 1), box-shadow 0.4s ease';
                card.style.transform = '';
                card.style.boxShadow = '';
                leaveTimer = setTimeout(() => { card.style.transition = ''; }, 650);
            });
        });
    }

    /* ── Hand of sheets (accessibility band) ────────────── */
    // Stacked as one sheet until the band is on screen, then fanned; after
    // that a different sheet is picked up every couple of seconds, so each
    // layout gets its moment without anybody having to hover. With the pointer
    // over the hand the cycle stops and the sheet nearest the pointer, left to
    // right, is the one held up.
    document.querySelectorAll('.lp-hand-fan').forEach(fan => {
        const sheets = [...fan.querySelectorAll('.lp-sheet')];
        if (reduceMotion || !('IntersectionObserver' in window)) {
            fan.classList.add('is-fanned');
            return;
        }
        let timer = 0, i = -1, visible = false, held = false;
        const pick = n => {
            i = n;
            sheets.forEach((sh, k) => sh.classList.toggle('is-up', k === n));
        };
        const next = () => pick((i + 1) % sheets.length);
        const run = () => {
            clearInterval(timer);
            if (visible && !held) timer = setInterval(next, 2400);
        };
        new IntersectionObserver(entries => {
            visible = entries[0].isIntersecting;
            if (visible && !fan.classList.contains('is-fanned')) {
                fan.classList.add('is-fanned');
                setTimeout(next, 1100);
            }
            run();
        }, { threshold: 0.5 }).observe(fan);
        fan.addEventListener('pointermove', e => {
            if (!fan.classList.contains('is-fanned')) return;
            if (!held) { held = true; run(); }
            // Nearest sheet by horizontal centre. Lifting only moves a sheet
            // vertically, so the answer never changes under a still pointer.
            let best = 0, bestD = Infinity;
            sheets.forEach((sh, k) => {
                const r = sh.getBoundingClientRect();
                const d = Math.abs(e.clientX - (r.left + r.width / 2));
                if (d < bestD) { bestD = d; best = k; }
            });
            if (best !== i) pick(best);
        });
        fan.addEventListener('pointerleave', () => { held = false; run(); });
    });

    /* ── Pilot band: the staff list becoming accounts ───── */
    // One teacher at a time: waiting → sending (the envelope flies) → ready,
    // with the count in the title bar keeping up. Holds on "all ready", then
    // quietly resets and goes again. Only runs while the band is on screen.
    document.querySelectorAll('.lp-staff').forEach(win => {
        const rows = [...win.querySelectorAll('.lp-staff-list li')];
        const count = win.querySelector('.lp-staff-count i');
        const setState = (li, state) => {
            li.classList.toggle('is-sending', state === 'sending');
            li.classList.toggle('is-ready', state === 'ready');
            li.querySelector('.lp-staff-state em').textContent =
                state === 'ready' ? 'Ready' : state === 'sending' ? 'Sending\u2026' : 'Waiting';
        };
        const setCount = n => { if (count) count.textContent = n; };

        if (reduceMotion || !('IntersectionObserver' in window)) {
            rows.forEach(li => setState(li, 'ready'));
            setCount(rows.length);
            return;
        }

        let timers = [], running = false;
        const later = (fn, ms) => timers.push(setTimeout(fn, ms));
        function cycle() {
            rows.forEach(li => setState(li, 'waiting'));
            setCount(0);
            rows.forEach((li, k) => {
                const at = 500 + k * 850;
                later(() => setState(li, 'sending'), at);
                later(() => { setState(li, 'ready'); setCount(k + 1); }, at + 600);
            });
            later(cycle, 500 + rows.length * 850 + 3600);
        }
        new IntersectionObserver(entries => {
            const on = entries[0].isIntersecting;
            if (on && !running) { running = true; cycle(); }
            if (!on && running) {
                running = false;
                timers.forEach(clearTimeout);
                timers = [];
            }
        }, { threshold: 0.4 }).observe(win);
    });

    /* ── Studios ────────────────────────────────────────── */
    // An app window with a list of resources down the side and the chosen one
    // on the canvas (.ta-tabs is the tab list; each tab's aria-controls names
    // its panel). Cycles by itself while on screen, until somebody picks a
    // tab; arrow keys move between tabs. Under reduced motion it only moves
    // when asked.
    document.querySelectorAll('.ta-tabs[role="tablist"]').forEach(list => {
        const tabs = Array.from(list.querySelectorAll('[role="tab"]'));
        const panels = tabs.map(t => document.getElementById(t.getAttribute('aria-controls')));
        if (!tabs.length || panels.some(p => !p)) return;
        let current = 0;
        let timer = null;
        let chosen = false;

        function select(i, focus) {
            current = i;
            tabs.forEach((t, k) => {
                const on = k === i;
                t.classList.toggle('is-active', on);
                t.setAttribute('aria-selected', String(on));
                t.tabIndex = on ? 0 : -1;
                panels[k].classList.toggle('is-active', on);
                panels[k].setAttribute('aria-hidden', String(!on));
            });
            if (focus) tabs[i].focus();
        }

        function stop() { clearInterval(timer); timer = null; }
        function start() {
            if (chosen || reduceMotion || timer) return;
            timer = setInterval(() => select((current + 1) % tabs.length), 4800);
        }

        tabs.forEach((t, i) => t.addEventListener('click', () => { chosen = true; stop(); select(i); }));
        list.addEventListener('keydown', e => {
            const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
            if (!step) return;
            e.preventDefault();
            chosen = true;
            stop();
            select((current + step + tabs.length) % tabs.length, true);
        });

        const stage = panels[0].parentElement;
        if ('IntersectionObserver' in window) {
            new IntersectionObserver(entries => {
                entries.forEach(e => (e.isIntersecting ? start() : stop()));
            }, { threshold: 0.4 }).observe(stage);
        } else {
            start();
        }
    });

    /* ── Hero doodles ───────────────────────────────────── */
    const canvas = document.getElementById('lp-doodles');
    const hero = canvas && canvas.parentElement;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    // Cheap deterministic noise, so each "boil" frame of a doodle is the same
    // wobble every time it comes round – that is what reads as hand-animated
    // rather than as jitter.
    function noise(a, b, c) {
        const x = Math.sin(a * 127.1 + b * 311.7 + c * 74.7) * 43758.5453;
        return (x - Math.floor(x)) * 2 - 1;
    }

    // Each shape is a list of strokes; each stroke a list of [x, y] points
    // around 0,0, joined with smooth curves.
    function starPts(r) {
        const pts = [];
        for (let i = 0; i <= 10; i++) {
            const a = -Math.PI / 2 + i * Math.PI / 5;
            const rr = i % 2 ? r * 0.45 : r;
            pts.push([Math.cos(a) * rr, Math.sin(a) * rr]);
        }
        return [pts];
    }
    const SHAPES = {
        star: { strokes: starPts(11), fill: 'rgba(244, 180, 71, 0.55)', sharp: true },
        sparkle: {
            strokes: [[[0, -10], [0, 10]], [[-10, 0], [10, 0]], [[-5, -5], [5, 5]], [[-5, 5], [5, -5]]],
        },
        squiggle: {
            strokes: [Array.from({ length: 9 }, (_, i) => [i * 6 - 24, i % 2 ? -5 : 5])],
        },
        spiral: {
            strokes: [Array.from({ length: 22 }, (_, i) => {
                const a = i * 0.55, r = 1.5 + i * 0.75;
                return [Math.cos(a) * r, Math.sin(a) * r];
            })],
        },
        bulb: {
            strokes: [
                Array.from({ length: 12 }, (_, i) => {
                    const a = Math.PI * 0.75 + i * (Math.PI * 1.5 / 11);
                    return [Math.cos(a) * 9, Math.sin(a) * 9 - 4];
                }),
                [[-4, 8], [4, 8]], [[-3, 11], [3, 11]],
                [[0, -19], [0, -23]], [[-13, -11], [-16, -14]], [[13, -11], [16, -14]],
            ],
            fill: 'rgba(244, 180, 71, 0.4)',
        },
        ring: {
            strokes: [Array.from({ length: 14 }, (_, i) => {
                const a = i * Math.PI * 2 / 12;
                return [Math.cos(a) * 7, Math.sin(a) * 7];
            })],
        },
        plus: { strokes: [[[0, -6], [0, 6]], [[-6, 0], [6, 0]]] },
    };

    let doodles = [];
    let W = 0, H = 0;

    // Placed around the real layout rather than at fixed fractions, so they
    // sit in the empty paper and never under a word.
    function layout() {
        const hr = hero.getBoundingClientRect();
        const at = el => {
            const r = el.getBoundingClientRect();
            return { l: r.left - hr.left, t: r.top - hr.top, r: r.right - hr.left, b: r.bottom - hr.top, w: r.width, h: r.height };
        };
        const copyEl = hero.querySelector('.lp-hero-copy');
        const demoEl = hero.querySelector('.lp-demo');
        const h1El = hero.querySelector('.lp-h1');
        if (!copyEl || !demoEl || !h1El) return [];
        const copy = at(copyEl), demo = at(demoEl), h1 = at(h1El);
        const sideBySide = demo.t < copy.b - 100;
        const list = [];
        // The line boxes of the copy's actual words (not its element boxes,
        // which include empty space a doodle may use).
        const textLines = el => {
            const out = [];
            const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
            const range = document.createRange();
            for (let n = walker.nextNode(); n; n = walker.nextNode()) {
                if (!n.textContent.trim()) continue;
                range.selectNodeContents(n);
                for (const r of range.getClientRects()) {
                    out.push({ l: r.left - hr.left, t: r.top - hr.top, r: r.right - hr.left, b: r.bottom - hr.top });
                }
            }
            return out;
        };
        const words = textLines(copyEl);
        const onWord = (x, y) => words.some(r => x > r.l - 14 && x < r.r + 14 && y > r.t - 14 && y < r.b + 14);
        const add = (shape, x, y, depth, rot, scale) => {
            // Only where it fits whole and clear of the words: a doodle cut
            // off at the canvas edge, or scribbled over a letter, reads as a bug.
            const m = 26 * (scale || 1);
            if (x < m || x > W - m || y < m || y > H - m || onWord(x, y)) return;
            list.push({
                shape, x, y, depth, rot: rot || 0, scale: scale || 1,
                phase: Math.random() * Math.PI * 2, id: list.length + 1,
            });
        };

        // A sparkle in the headline's own white space; if the words fill
        // that spot, just past the end of the longest line instead.
        let sx = h1.l + h1.w * 0.74, sy = h1.t + h1.h * 0.46;
        if (onWord(sx, sy)) {
            const h1Lines = textLines(h1El);
            const longest = h1Lines.reduce((a, r) => (r.r > a.r ? r : a), h1Lines[0]);
            if (longest) { sx = longest.r + 30; sy = longest.t + 8; }
        }
        add('sparkle', sx, sy, 0.6, 0.2, 0.9);
        if (sideBySide) {
            add('star', demo.l + demo.w * 0.18, demo.t - 58, 0.9, -0.2);
            add('squiggle', demo.l + demo.w * 0.48, demo.t - 84, 0.5, -0.15);
            add('bulb', Math.min(W - 30, demo.r - demo.w * 0.12), demo.t - 110, 0.8, 0.15);
            add('plus', demo.l - 26, demo.b - 40, 0.4);
            add('ring', demo.l + demo.w * 0.7, demo.t - 40, 0.35);
        }
        const leftRoom = copy.l, rightRoom = W - demo.r;
        if (leftRoom > 90) {
            add('spiral', leftRoom * 0.45, copy.t + copy.h * 0.28, 0.7, 0, 1.1);
            add('plus', leftRoom * 0.55, copy.t + copy.h * 0.7, 0.4);
        }
        if (sideBySide && rightRoom > 90) {
            add('sparkle', demo.r + rightRoom * 0.5, demo.t + demo.h * 0.3, 0.8, 0.3);
            add('star', demo.r + rightRoom * 0.45, demo.b - 10, 0.5, 0.4, 0.8);
        }
        return list;
    }

    // The paper plane's route: along the empty strip above the kicker, with
    // one loop that dips into the space above the demo window.
    let plane = null;
    let planeIn = 3.5;
    function launch() {
        const demoEl = hero.querySelector('.lp-demo');
        const hr = hero.getBoundingClientRect();
        const dr = demoEl ? demoEl.getBoundingClientRect() : null;
        const sideBySide = dr && dr.top - hr.top < H * 0.5;
        const y = 26;
        const loopX = sideBySide ? dr.left - hr.left + dr.width * 0.4 : W * 0.6;
        const dir = Math.random() < 0.7 ? 1 : -1;
        plane = {
            s: 0, dir, y,
            dur: Math.max(4.5, W / 260),
            loopAt: clamp01((dir > 0 ? loopX + 60 : W - loopX + 60) / (W + 120) - 0.06),
            trail: [],
        };
    }
    const clamp01 = v => Math.max(0, Math.min(1, v));

    function planePos(p, s) {
        let x = -60 + s * (W + 120);
        let y = p.y + Math.sin(s * Math.PI * 3) * 8;
        const L = 0.13;
        if (s > p.loopAt && s < p.loopAt + L) {
            const th = (s - p.loopAt) / L * Math.PI * 2;
            const R = 30;
            x += Math.sin(th) * R;
            y += (1 - Math.cos(th)) * R;
        }
        if (p.dir < 0) x = W - x;
        return [x, y];
    }

    function smoothStroke(pts) {
        ctx.beginPath();
        ctx.moveTo(pts[0][0], pts[0][1]);
        for (let i = 1; i < pts.length - 1; i++) {
            const mx = (pts[i][0] + pts[i + 1][0]) / 2, my = (pts[i][1] + pts[i + 1][1]) / 2;
            ctx.quadraticCurveTo(pts[i][0], pts[i][1], mx, my);
        }
        const last = pts[pts.length - 1];
        ctx.lineTo(last[0], last[1]);
    }

    function drawDoodle(d, t, boil, px, py) {
        const shape = SHAPES[d.shape];
        const bob = reduceMotion ? 0 : Math.sin(t * 0.9 + d.phase) * 3;
        ctx.save();
        ctx.translate(d.x + px * d.depth, d.y + py * d.depth + bob);
        ctx.rotate(d.rot + (reduceMotion ? 0 : Math.sin(t * 0.6 + d.phase) * 0.06));
        ctx.scale(d.scale, d.scale);
        ctx.strokeStyle = 'rgba(30, 27, 46, 0.42)';
        ctx.lineWidth = 2 / d.scale;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        shape.strokes.forEach((stroke, si) => {
            const pts = stroke.map(([x, y], pi) => [
                x + noise(d.id, boil, si * 31 + pi) * 1.1,
                y + noise(d.id + 9, boil, si * 17 + pi) * 1.1,
            ]);
            if (shape.sharp) {
                ctx.beginPath();
                pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
            } else {
                smoothStroke(pts);
            }
            if (shape.fill && si === 0) {
                ctx.fillStyle = shape.fill;
                ctx.fill();
            }
            ctx.stroke();
        });
        ctx.restore();
    }

    function drawPlane(p) {
        // Dashed trail, fading out behind it.
        const tr = p.trail;
        ctx.save();
        ctx.lineWidth = 1.6;
        ctx.lineCap = 'round';
        ctx.setLineDash([5, 7]);
        for (let i = 1; i < tr.length; i++) {
            ctx.strokeStyle = 'rgba(30, 27, 46, ' + (0.35 * i / tr.length).toFixed(3) + ')';
            ctx.beginPath();
            ctx.moveTo(tr[i - 1][0], tr[i - 1][1]);
            ctx.lineTo(tr[i][0], tr[i][1]);
            ctx.stroke();
        }
        ctx.restore();

        const [x, y] = planePos(p, p.s);
        const [x2, y2] = planePos(p, Math.min(1, p.s + 0.004));
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(Math.atan2(y2 - y, x2 - x));
        ctx.lineWidth = 1.8;
        ctx.lineJoin = 'round';
        ctx.strokeStyle = INK;
        ctx.fillStyle = '#fffdf8';
        ctx.beginPath();
        ctx.moveTo(15, 0);
        ctx.lineTo(-11, -9);
        ctx.lineTo(-6, 0);
        ctx.lineTo(-11, 8);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = '#dfe6ff';
        ctx.beginPath();
        ctx.moveTo(15, 0);
        ctx.lineTo(-6, 0);
        ctx.lineTo(-9, 4);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        ctx.restore();
    }

    function resize() {
        const w = canvas.clientWidth, h = canvas.clientHeight;
        if (!w || !h) return false;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        canvas.width = Math.round(w * dpr);
        canvas.height = Math.round(h * dpr);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        W = w; H = h;
        doodles = layout();
        return true;
    }

    // Parallax: the doodles drift away from the pointer, nearer ones more.
    let pointer = null;
    let par = { x: 0, y: 0 };
    let t = 0;

    function render(dt) {
        t += dt;
        const boil = Math.floor(t * 7) % 3;
        const tx = pointer ? (pointer.x - W / 2) * -0.025 : 0;
        const ty = pointer ? (pointer.y - H / 2) * -0.025 : 0;
        par.x += (tx - par.x) * Math.min(1, dt * 3);
        par.y += (ty - par.y) * Math.min(1, dt * 3);

        ctx.clearRect(0, 0, W, H);
        doodles.forEach(d => drawDoodle(d, t, boil, par.x, par.y));

        if (reduceMotion) return;
        if (!plane) {
            planeIn -= dt;
            if (planeIn <= 0) launch();
        } else {
            plane.s += dt / plane.dur;
            if (plane.s >= 1) {
                plane = null;
                planeIn = 8 + Math.random() * 7;
            } else {
                plane.trail.push(planePos(plane, plane.s));
                if (plane.trail.length > 70) plane.trail.shift();
                drawPlane(plane);
            }
        }
    }

    let raf = 0, last = 0, onScreen = false;
    function frame(now) {
        raf = 0;
        const dt = Math.min(1 / 30, (now - last) / 1000 || 0);
        last = now;
        if (!W) return;
        render(dt);
        if (onScreen && !document.hidden) raf = requestAnimationFrame(frame);
    }
    function kick() {
        if (reduceMotion || raf || !onScreen || document.hidden) return;
        last = performance.now();
        raf = requestAnimationFrame(frame);
    }

    if ('ResizeObserver' in window) {
        // Watches the hero, not just the canvas: the copy reflowing (the flip
        // word, fonts arriving) moves the empty space the doodles sit in.
        new ResizeObserver(() => {
            if (resize()) {
                if (reduceMotion) render(0);
                else kick();
            }
        }).observe(hero);
    }
    document.fonts && document.fonts.ready.then(() => { if (resize() && reduceMotion) render(0); });

    if ('IntersectionObserver' in window) {
        new IntersectionObserver(entries => {
            onScreen = entries[0].isIntersecting;
            kick();
        }).observe(canvas);
    }

    if (reduceMotion) return;
    document.addEventListener('visibilitychange', kick);
    hero.addEventListener('pointermove', e => {
        const r = canvas.getBoundingClientRect();
        pointer = { x: e.clientX - r.left, y: e.clientY - r.top };
    }, { passive: true });
    hero.addEventListener('pointerleave', () => { pointer = null; });
})();
